import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";
import { BUSINESS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm({ defaultService, defaultCity }) {
  const { services: SERVICES, cities: CITIES, ui, lang } = useLang();
  const q = ui.quote;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: defaultService || SERVICES[0].name,
    city: defaultCity || CITIES[0].name,
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErr("");
    try {
      await axios.post(`${API}/quote`, form);
      setStatus("success");
      toast.success(
        lang === "fr"
          ? "Demande reçue — nous répondrons dans l'heure ouvrable."
          : "Request received — we'll respond within one business hour."
      );
      setForm({
        name: "",
        email: "",
        phone: "",
        service: defaultService || SERVICES[0].name,
        city: defaultCity || CITIES[0].name,
        message: "",
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail?.[0]?.msg || "Unable to send request. Please try again.";
      setErr(msg);
      setStatus("error");
      toast.error(msg);
    }
  };

  const bind = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  return (
    <section
      id="quote"
      className="relative border-t border-border bg-background"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
              {q.chip}
            </div>
            <h2 className="font-serif font-light text-4xl md:text-7xl leading-[0.92] tracking-tighter mb-8">
              {q.title1} <br />
              <i className="text-primary">{q.title2}</i>
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-8 max-w-md">
              {q.intro}
            </p>
            <div className="font-mono text-xs tracking-widest space-y-3">
              <div className="flex justify-between items-center border-b border-border pb-3">
                <span className="text-muted-foreground uppercase">{lang === "fr" ? "Téléphone" : "Phone"}</span>
                <a href={BUSINESS.phoneHref} className="hover:text-primary transition-colors">{BUSINESS.phone}</a>
              </div>
              <div className="flex justify-between items-center border-b border-border pb-3">
                <span className="text-muted-foreground uppercase">{lang === "fr" ? "Courriel" : "Email"}</span>
                <a href={BUSINESS.emailHref} className="hover:text-primary transition-colors">{BUSINESS.email}</a>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <span className="text-muted-foreground uppercase">{lang === "fr" ? "Déploiement" : "Deploy"}</span>
                <span>{lang === "fr" ? "4 h · province" : "4 hrs · province-wide"}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <span className="text-muted-foreground uppercase">{lang === "fr" ? "Langue" : "Language"}</span>
                <span>{lang === "fr" ? "Anglais / Français" : "English / French"}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <span className="text-muted-foreground uppercase">{lang === "fr" ? "Licence" : "License"}</span>
                <span>{lang === "fr" ? "Classe 2 N.-B." : "NB Class 2"}</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={IDS.quote.success}
                className="border border-primary p-10 md:p-14"
              >
                <CheckCircle2 size={40} strokeWidth={1.2} className="text-primary mb-6" />
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
                  {q.success}
                </h3>
                <p className="text-foreground/70 max-w-md leading-relaxed mb-6">
                  {q.successBody} {lang === "fr"
                    ? `Pour toute urgence, appelez le ${BUSINESS.phone}.`
                    : `For urgent matters, call ${BUSINESS.phone}.`}
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="font-mono text-xs tracking-[0.2em] uppercase border border-foreground px-6 py-3 hover:bg-primary hover:border-primary transition-colors"
                >
                  {q.another}
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid={IDS.quote.form}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10"
              >
                <Field label={q.fields.name}>
                  <input
                    {...bind("name")}
                    required
                    data-testid={IDS.quote.name}
                    className="input-base"
                  />
                </Field>
                <Field label={q.fields.email}>
                  <input
                    {...bind("email")}
                    type="email"
                    required
                    data-testid={IDS.quote.email}
                    className="input-base"
                  />
                </Field>
                <Field label={q.fields.phone}>
                  <input
                    {...bind("phone")}
                    required
                    data-testid={IDS.quote.phone}
                    className="input-base"
                  />
                </Field>
                <Field label={q.fields.city}>
                  <select
                    {...bind("city")}
                    data-testid={IDS.quote.city}
                    className="input-base"
                  >
                    {CITIES.map((c) => (
                      <option key={c.slug} value={c.name} className="bg-background">
                        {c.name}
                      </option>
                    ))}
                    <option value="Other NB Location" className="bg-background">
                      {lang === "fr" ? "Autre lieu au N.-B." : "Other NB Location"}
                    </option>
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label={q.fields.service}>
                    <select
                      {...bind("service")}
                      data-testid={IDS.quote.service}
                      className="input-base"
                    >
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.name} className="bg-background">
                          {s.name}
                        </option>
                      ))}
                      <option value="Consultation / Assessment" className="bg-background">
                        {lang === "fr" ? "Consultation / Évaluation" : "Consultation / Assessment"}
                      </option>
                    </select>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label={q.fields.message}>
                    <textarea
                      {...bind("message")}
                      rows={4}
                      data-testid={IDS.quote.message}
                      className="input-base resize-none"
                      placeholder={q.placeholders.message}
                    />
                  </Field>
                </div>

                {err && (
                  <div
                    data-testid={IDS.quote.error}
                    className="md:col-span-2 border border-destructive/60 p-4 font-mono text-xs tracking-widest text-destructive"
                  >
                    {err}
                  </div>
                )}

                <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground max-w-sm">
                    {q.consent}
                  </p>
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid={IDS.quote.submit}
                    className="group inline-flex items-center gap-3 bg-foreground text-background px-8 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-primary hover:text-white transition-colors disabled:opacity-60"
                  >
                    {status === "loading" ? q.submitting : q.submit}
                    <Send size={14} strokeWidth={1.4} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input-base {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid hsl(var(--border));
          padding: 12px 0;
          color: hsl(var(--foreground));
          font-family: 'Manrope', sans-serif;
          font-size: 16px;
          outline: none;
          transition: border-color 0.3s;
        }
        .input-base:focus {
          border-bottom-color: hsl(var(--primary));
        }
        .input-base::placeholder {
          color: hsl(var(--muted-foreground));
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}
